import React, { useState, useEffect } from 'react';
import { Box, Paper, Grid, Typography, LinearProgress, Select, MenuItem, CircularProgress } from '@mui/material';
import axios from 'axios';
import API_BASE_URL from '../../config';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';

const AdminDashboard = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [timeRange, setTimeRange] = useState('1h');
  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);

  const fetchAdminDashboard = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setStats(response.data);

      
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/');
      }
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchChartData = async () => {

      try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_BASE_URL}/admin/dashboard/chart`, {
              params: { range: timeRange },
              headers: {
                  'Authorization': `Bearer ${token}`
              }
          });
          setChartData(response.data);
      } catch (error) {
          console.error('Chart data error:', error);
      }
  };

  const handleRefresh = () => {
    // set loading 
    setLoadingChart(true);
    fetchAdminDashboard();
    fetchChartData();
    setLoadingChart(false);
  };

  useEffect(() => {
    fetchAdminDashboard();
  }, []);
  
  useEffect(() => {    
    fetchChartData();
  }, [timeRange]);
  
  const statCards = [
    { title: 'Admins', value: 'admin_count', key: 'admins' },
    { title: 'Organizations', value: 'org_count', key: 'orgs' },
    { title: 'Members', value: 'org_member_count', key: 'members' },
    { title: 'Orders', value: 'order_count', key: 'orders' },
    { title: 'Total Jobs', value: 'job_count', key: 'jobs' },
    { title: 'Active Jobs', value: 'active_job_count', key: 'activeJobs' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Stat Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.key}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6">{card.title}</Typography>
              {stats ? (
                <>
                  <Typography variant="h4">
                    {stats[card.value]}
                    <Typography 
                      component="span" 
                      variant="body2" 
                      color={stats[card.value + '_24h'] > 0 ? "success.main" : "text.secondary"}
                      sx={{ ml: 1 }}
                    >
                      {stats[card.value + '_24h'] >= 0 ? '+' : '-'}{stats[card.value + '_24h']} (24h)
                    </Typography>
                  </Typography>

                </>
              ) : (
                <LinearProgress />
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleRefresh}
            startIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="1h">1 Hour</MenuItem>
            <MenuItem value="6h">6 Hours</MenuItem>
            <MenuItem value="12h">12 Hours</MenuItem>
            <MenuItem value="1d">1 Day</MenuItem>
            <MenuItem value="7d">7 Days</MenuItem>
            <MenuItem value="30d">30 Days</MenuItem>
          </Select>
        </Box>
        
        {loadingChart ? (
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="formatted_time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="admin_count" stroke="#8884d8" activeDot={{ r: 8 }} />
                <Line type="monotone" dataKey="org_count" stroke="#82ca9d" />
                <Line type="monotone" dataKey="org_member_count" stroke="#ffc658" />
                <Line type="monotone" dataKey="order_count" stroke="#ff8042" />
                <Line type="monotone" dataKey="job_count" stroke="#0088FE" />
                <Line type="monotone" dataKey="active_job_count" stroke="#00C49F" />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Box>
    </Box>
    
  );
};

export default AdminDashboard;